import { useEffect } from "react";
import { QueryClient, QueryKey, useQueryClient } from "@tanstack/react-query";
import { OrderChat, OrderChatUnreadSummary } from "@core/types";
import { supabaseClient } from "../../parhato2/src/shared/supabase";
import { convertOrderChat } from "../../parhato2/src/shared/clients/converters";
import { orderChatQueryKeys } from "./keys";
import { upsertSummaryItem, removeSummaryItem, incrementSummaryItemUnread } from "./summaryCache";

type OrderChatsPayload = {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: Record<string, unknown>;
  old: { id?: number };
};

type OrderChatMessagesPayload = {
  new: {
    chat_id?: number;
    sender_type?: string;
    sender_customer_id?: number;
  };
};

const handleOrderChatRowChange = (
  queryClient: QueryClient,
  summaryKey: QueryKey,
  payload: OrderChatsPayload,
) => {
  if (payload.eventType === "DELETE") {
    const oldId = Number(payload.old.id);
    if (!oldId) return;
    queryClient.setQueryData<OrderChatUnreadSummary>(summaryKey, (prev) => removeSummaryItem(prev, oldId));
    return;
  }

  const chat = convertOrderChat(payload.new);
  if (!chat.id) return;

  queryClient.setQueryData<OrderChatUnreadSummary>(summaryKey, (prev) =>
    chat.status === "open" ? upsertSummaryItem(prev, chat) : removeSummaryItem(prev, chat.id),
  );

  if (chat.orderId) {
    queryClient.setQueryData<OrderChat | null>(orderChatQueryKeys.byOrder(chat.orderId), (prev) =>
      prev ? { ...prev, ...chat } : prev,
    );
  }
};

const handleOrderChatMessageInsert = (
  queryClient: QueryClient,
  summaryKey: QueryKey,
  customerId: number,
  payload: OrderChatMessagesPayload,
) => {
  const row = payload.new;
  const chatId = Number(row.chat_id);
  if (!chatId) return;

  // Own messages are already in cache via the send mutation's onSuccess.
  const isOwnMessage = row.sender_type === "customer" && Number(row.sender_customer_id) === customerId;
  if (isOwnMessage) return;

  queryClient.setQueryData<OrderChatUnreadSummary>(summaryKey, (prev) => incrementSummaryItemUnread(prev, chatId));
  queryClient.invalidateQueries({
    queryKey: orderChatQueryKeys.messages(chatId),
    refetchType: "active",
  });
};

const useChatRealtime = (customerId: number | undefined, enabled: boolean) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!customerId || !enabled) return;

    const summaryKey = orderChatQueryKeys.unreadSummary(customerId);

    const channel = supabaseClient
      .channel(`chat-${customerId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "order_chats", filter: `customer_id=eq.${customerId}` },
        (payload) => handleOrderChatRowChange(queryClient, summaryKey, payload as OrderChatsPayload),
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "order_chat_messages" },
        (payload) =>
          handleOrderChatMessageInsert(queryClient, summaryKey, customerId, payload as OrderChatMessagesPayload),
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          queryClient.invalidateQueries({ queryKey: summaryKey });
        }
      });

    return () => {
      supabaseClient.removeChannel(channel);
    };
  }, [customerId, enabled, queryClient]);
};

export default useChatRealtime;
